import React, { useState } from "react";

import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text
} from "@chakra-ui/react";

import { useDispatch, useSelector } from "react-redux";

import { WhatsappIntegrationStages } from "@/jenesys-types/data/enums";
import {
  TeamMember,
  WhatsappConnectionStatus
} from "@/jenesys-types/jenesys-entities/config";

import { useCompany } from "@/hooks/util/useCompany";
import { selectCurrentUser } from "@/store/app/bootstrap/bootstrap";
import {
  loadWhatsappStatus,
  selectConnectionsSettings,
  submitWpNumber,
  updateWhatsappNumber
} from "@/store/features/clientSettings/connections/connections";
import { loadTeamMembers } from "@/store/features/clientSettings/team/team";
import { toastError } from "@/util/helpers";

import { EnterWhatsappNumber } from "./EnterWhatsappNumber";
import { VerifyWhatsappNumber } from "./VerifyWhatsappNumber";

interface IntegrateWhatsappProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string | undefined;
  selectedMember: TeamMember | null;
  whatsappStatus: WhatsappConnectionStatus;
}

export const IntegrateWhatsapp: React.FC<IntegrateWhatsappProps> = ({
  isOpen,
  onClose,
  clientId,
  selectedMember,
  whatsappStatus
}) => {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);

  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState(WhatsappIntegrationStages.ENTER_NUMBER);
  const [knowsWhatsappNumber, setKnowsWhatsappNumber] = useState(true);
  const { activeClientId } = useCompany();

  const isForSelf = !selectedMember;
  const { isSubmittingNumber } = useSelector(selectConnectionsSettings);

  const handleCloseModal = () => {
    onClose();
    setStep(WhatsappIntegrationStages.ENTER_NUMBER);
    setPhone("");
    setCode("");
  };

  const handleSubmitNumber = () => {
    dispatch(
      submitWpNumber({
        clientId: activeClientId!!,
        userId: selectedMember?.user?.id || user?.id,
        number: `+${phone}`,
        onSuccess: () => {
          setStep(WhatsappIntegrationStages.SUCCESS);
          dispatch(loadWhatsappStatus());
          dispatch(
            loadTeamMembers(clientId as string, {
              searchQuery: "",
              page: 0
            })
          );
        },
        onError: (error) => {
          toastError("Failed to connect WhatsApp", error);
        }
      })
    );
  };

  const sendInvitationWithoutNumber = () => {
    dispatch(
      submitWpNumber({
        clientId: activeClientId!!,
        userId: selectedMember?.user?.id,
        number: null,
        onSuccess: () => {
          setStep(WhatsappIntegrationStages.SUCCESS);
          dispatch(
            loadTeamMembers(clientId as string, {
              searchQuery: "",
              page: 0
            })
          );
        },
        onError: (error) => {
          toastError("Failed to send invitation", error);
        }
      })
    );
  };

  const handleUpdateWhatsappNumber = () => {
    if (!whatsappStatus.id) {
      toastError("WhatsApp connection ID is missing");
      return;
    }
    dispatch(
      updateWhatsappNumber({
        connectionId: whatsappStatus.id,
        number: `+${phone}`,
        onSuccess: () => {
          setStep(WhatsappIntegrationStages.SUCCESS);
          dispatch(loadWhatsappStatus());
        },
        onError: (error) => {
          toastError("Failed to connect WhatsApp", error);
        }
      })
    );
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (isForSelf && whatsappStatus?.status === "invited") {
      handleUpdateWhatsappNumber();
    } else if (knowsWhatsappNumber) {
      handleSubmitNumber();
    } else {
      setStep(WhatsappIntegrationStages.UNKNOWN_NUMBER_INVITE);
    }
  };

  let content = (
    <EnterWhatsappNumber
      phone={phone}
      setPhone={setPhone}
      isForSelf={isForSelf}
      isSubmittingNumber={isSubmittingNumber}
      knowsWhatsappNumber={knowsWhatsappNumber}
      handleSubmitNumber={handleSubmit}
      setKnowsWhatsappNumber={setKnowsWhatsappNumber}
    />
  );

  if (step === WhatsappIntegrationStages.VERIFY_NUMBER) {
    content = (
      <VerifyWhatsappNumber
        code={code}
        phone={phone}
        clientId={clientId}
        setCode={setCode}
        setStep={setStep}
      />
    );
  }

  if (step === WhatsappIntegrationStages.SUCCESS) {
    content = (
      <ModalContent fontWeight={500} p={12}>
        <ModalHeader p={0}>
          <Heading fontSize={20} color="secondary.600" fontWeight={600}>
            Successfully connected to Jack
          </Heading>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pt={6} px={0}>
          <Text fontSize={14}>
            {isForSelf
              ? "You have successfully linked your WhatsApp number to Jack. You should get a WhatsApp message from Jack to start sending your documents. Congrats!"
              : `You have successfully invited ${selectedMember?.user?.first_name} ${selectedMember?.user?.last_name} to connect Jack to their WhatsApp. When they have accepted, the WhatsApp icon will turn green and their status will change to connected.`}
          </Text>
        </ModalBody>
        <ModalFooter mt={4} p={0} justifyContent="flex-start">
          <Button onClick={handleCloseModal} variant="outline" w={85} h={12}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    );
  }

  if (step === WhatsappIntegrationStages.UNKNOWN_NUMBER_INVITE) {
    content = (
      <ModalContent fontWeight={500} p={12}>
        <ModalHeader p={0}>
          <Heading fontSize={20} color="secondary.600" fontWeight={600}>
            Send Invitation
          </Heading>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pt={6} px={0}>
          <Text fontSize={14}>
            We will send an invitation email to {selectedMember?.user?.email} to
            help them connect their WhatsApp account to Jack. Click &quot;Send
            Invitation&quot; to proceed.
          </Text>
        </ModalBody>
        <ModalFooter mt={4} p={0}>
          <Button
            isLoading={isSubmittingNumber}
            onClick={sendInvitationWithoutNumber}
            size="sm"
            h={12}
          >
            Send Invitation
          </Button>
        </ModalFooter>
      </ModalContent>
    );
  }
  return (
    <Modal
      size="2xl"
      closeOnOverlayClick={false}
      onClose={handleCloseModal}
      isOpen={isOpen}
      isCentered
    >
      <ModalOverlay />
      {content}
    </Modal>
  );
};
